import React, { useRef } from 'react';
import { cn } from './model/derive';
import TdButton from './Button';
import useRowMotion from './useRowMotion';
import './motion.css';

/**
 * Task List 항목 편집 (Add task · Change scope) — 번호 · 항목마다 한 줄 입력 + × · Enter 로 다음 항목 · 아래 "+ Add item"(점선 알약).
 * 항목이 없으면 빈 줄 하나에 "Add an item the receiver will check" 자리표시를 보여 준다.
 * 빈 줄은 저장할 때 빠진다 (cleanItems).
 *
 * @param items    문자열 배열 (빈 문자열 = 아직 안 쓴 줄) — textOf · withText · blankRow 를 주면 객체 줄도 된다
 * @param onChange (next: string[]) => void
 */
const TaskListEditor = ({
  id,
  items,
  onChange,
  ariaLabel = 'Task List',
  startAt = 0,
  placeholder = 'Add an item the receiver will check',
  textOf = (r) => r,
  withText = (_, v) => v,
  blankRow = '',
  isLocked,
  tagOf,
  canAdd = true,
}) => {
  const listRef = useRef(null);
  const rows = items.length ? items : [blankRow];
  const set = (i, v) => onChange(rows.map((x, j) => (j === i ? withText(x, v) : x)));
  const motion = useRowMotion(rows, onChange);
  const add = () => {
    motion.add(blankRow);
    // 새 줄로 바로 옮겨 간다
    window.requestAnimationFrame(() => {
      const inputs = listRef.current?.querySelectorAll('input');
      inputs?.[inputs.length - 1]?.focus();
    });
  };

  return (
    <div className='flex flex-col gap-2'>
      <ul ref={listRef} aria-label={ariaLabel} className='flex flex-col gap-2'>
        {rows.map((item, i) => {
          const text = textOf(item);
          const locked = !!isLocked?.(item);
          const tag = tagOf?.(item);
          return (
            <li
              key={motion.keys[i]}
              className={cn(
                'flex items-center gap-1.5',
                motion.isFresh(motion.keys[i]) && 'td-rise',
              )}
            >
              <span className='w-5 shrink-0 text-right text-[13px] tabular-nums text-text-secondary'>
                {startAt + i + 1}.
              </span>
              <input
                id={i === 0 ? id : undefined}
                value={text}
                maxLength={300}
                readOnly={locked}
                placeholder={i === 0 && rows.length === 1 ? placeholder : ''}
                aria-label={`Task List item ${startAt + i + 1}`}
                onChange={(e) => set(i, e.target.value)}
                onKeyDown={(e) => {
                  if (canAdd && e.key === 'Enter' && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                    add();
                  }
                }}
                className={cn(
                  'h-[38px] min-w-0 flex-1 rounded-lg border px-3 text-[14px] placeholder:text-text-placeholder focus:outline-none',
                  locked
                    ? 'border-transparent bg-green-deep03/[0.04] text-text-secondary'
                    : 'border-green-deep03/20 bg-grayscale-gray00 text-text-primary focus:border-green-deep02',
                )}
              />
              {tag && (
                <span className='inline-flex h-6 shrink-0 items-center rounded-full bg-grayscale-gray01 px-2.5 text-[12px] font-medium text-text-tertiary shadow-[inset_0_0_0_1px_#d5d2cd]'>
                  {tag}
                </span>
              )}
              {(rows.length > 1 || text) && (
                <button
                  type='button'
                  aria-label='Remove item'
                  title='Remove item'
                  onClick={(e) => motion.remove(i, e.currentTarget.closest('li'))}
                  className={cn(
                    'flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-lg text-[18px] leading-none text-text-secondary',
                    'hover:bg-green-deep03/[0.06] hover:text-text-primary',
                  )}
                >
                  ×
                </button>
              )}
            </li>
          );
        })}
      </ul>
      {canAdd && (
        <TdButton
          variant='ghost'
          size='s'
          onClick={add}
          className='ml-6 w-fit border-dashed border-green-deep03/40 px-3 text-green-deep02 hover:bg-surface-green10 hover:text-green-deep02'
        >
          + Add item
        </TdButton>
      )}
    </div>
  );
};

/** 저장할 항목 — 앞뒤 공백을 지우고 빈 줄은 뺀다 */
export const cleanItems = (items) => items.map((x) => x.trim()).filter(Boolean);

export default TaskListEditor;
